/**
 * Paleta do tenant: a partir de poucas cores-base (primária, primária no escuro e
 * cor do menu) deriva os tokens de `index.css` com contraste legível nos dois temas.
 * Funções puras, sem DOM; quem aplica é `TenantPalette`.
 */

export interface PaletteInput {
  /** Cor primária do tema claro (#RRGGBB). */
  primary?: string | null | undefined
  /** Cor primária própria do tema escuro; vazia deriva da primária. */
  primaryDark?: string | null | undefined
  /** Cor do menu lateral (#RRGGBB). */
  sidebar?: string | null | undefined
}

export type PaletteTokens = Record<string, string>

export interface Palette {
  light: PaletteTokens
  dark: PaletteTokens
}

interface Oklch {
  l: number
  c: number
  h: number
}

type Rgb = readonly [number, number, number]

const HEX_COLOR = /^#[0-9a-f]{6}$/i

/** Mínimo WCAG AA para texto normal. */
export const MIN_CONTRAST = 4.5

/** Luminosidade máxima do menu: ele é escuro nos dois temas e o texto claro depende disso. */
const SIDEBAR_MAX_L = 0.3
const DARK_PRIMARY_MIN_L = 0.71
const LIGHT_SIDEBAR_PRIMARY_MIN_L = 0.55

export function isHexColor(value: string | null | undefined): value is string {
  return typeof value === 'string' && HEX_COLOR.test(value)
}

const toLinear = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4)

function hexToLinear(hex: string): Rgb {
  const n = Number.parseInt(hex.slice(1), 16)
  return [toLinear(((n >> 16) & 255) / 255), toLinear(((n >> 8) & 255) / 255), toLinear((n & 255) / 255)]
}

function linearToOklch([r, g, b]: Rgb): Oklch {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  const lightness = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
  const chroma = Math.hypot(a, bb)

  return {
    l: lightness,
    c: chroma,
    // Cinza puro não tem matiz; 0 evita ruído numérico.
    h: chroma < 1e-4 ? 0 : ((Math.atan2(bb, a) * 180) / Math.PI + 360) % 360,
  }
}

/** RGB linear sem recorte (valores fora de 0..1 indicam fora da gama sRGB). */
function oklchToLinear({ l, c, h }: Oklch): Rgb {
  const a = c * Math.cos((h * Math.PI) / 180)
  const b = c * Math.sin((h * Math.PI) / 180)
  const l3 = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m3 = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s3 = (l - 0.0894841775 * a - 1.291485548 * b) ** 3

  return [
    4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3,
    -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3,
    -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3,
  ]
}

const EPSILON = 1e-4
const inGamut = (rgb: Rgb) => rgb.every((v) => v >= -EPSILON && v <= 1 + EPSILON)

/** Reduz o croma até caber em sRGB, mantendo luminosidade e matiz. */
function fitToGamut(color: Oklch): Oklch {
  if (inGamut(oklchToLinear(color))) return color

  let low = 0
  let high = color.c
  for (let i = 0; i < 20; i++) {
    const mid = (low + high) / 2
    if (inGamut(oklchToLinear({ ...color, c: mid }))) low = mid
    else high = mid
  }

  return { ...color, c: low }
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

function luminance(rgb: Rgb): number {
  const [r, g, b] = rgb.map((v) => clamp(v, 0, 1)) as [number, number, number]
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function contrast(a: Rgb, b: Rgb): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number]
  return (hi + 0.05) / (lo + 0.05)
}

/** Lê `#RRGGBB` ou `oklch(L C H)` (formatos que esta paleta produz) como RGB linear. */
function parseColor(value: string): Rgb {
  if (HEX_COLOR.test(value)) return hexToLinear(value)

  const match = /^oklch\(([\d.]+) ([\d.]+) ([\d.]+)\)$/.exec(value)
  if (!match) throw new Error(`Cor não suportada: ${value}`)

  return oklchToLinear({ l: Number(match[1]), c: Number(match[2]), h: Number(match[3]) })
}

/** Razão de contraste WCAG entre duas cores desta paleta. */
export function contrastRatio(a: string, b: string): number {
  return contrast(parseColor(a), parseColor(b))
}

function format(color: Oklch): string {
  const fitted = fitToGamut({ ...color, l: clamp(color.l, 0, 1) })
  return `oklch(${fitted.l.toFixed(3)} ${fitted.c.toFixed(3)} ${fitted.h.toFixed(1)})`
}

const WHITE = 'oklch(1.000 0.000 0.0)'

/** Texto sobre a cor de marca: branco ou um escuro do mesmo matiz, o que contrastar mais. */
function foregroundFor(background: string, hue: number): string {
  const dark = format({ l: 0.13, c: 0.02, h: hue })
  return contrastRatio(background, WHITE) >= contrastRatio(background, dark) ? WHITE : dark
}

function brandTokens(brand: Oklch, brandValue: string, dark: boolean): PaletteTokens {
  const { c, h } = brand
  const accent = dark
    ? { l: 0.33, c: clamp(c * 0.4, 0.03, 0.09), h }
    : { l: 0.94, c: clamp(c * 0.15, 0.01, 0.04), h }
  const accentForeground = dark ? { l: 0.81, c: Math.min(c, 0.1), h } : { l: 0.46, c: Math.min(c, 0.2), h }
  // O menu é escuro nos dois temas: o destaque da marca nele nunca pode ser escuro demais.
  const sidebarBrand = format({ ...brand, l: Math.max(brand.l, LIGHT_SIDEBAR_PRIMARY_MIN_L) })
  const sidebarBrandValue = dark ? brandValue : sidebarBrand

  return {
    '--primary': brandValue,
    '--primary-foreground': foregroundFor(brandValue, h),
    '--ring': brandValue,
    '--chart-1': brandValue,
    '--accent': format(accent),
    '--accent-foreground': format(accentForeground),
    '--sidebar-primary': sidebarBrandValue,
    '--sidebar-primary-foreground': foregroundFor(sidebarBrandValue, h),
    '--sidebar-ring': format({ l: DARK_PRIMARY_MIN_L, c: Math.min(c, 0.15), h }),
  }
}

function sidebarTokens(base: Oklch, dark: boolean): PaletteTokens {
  const limited = Math.min(base.l, SIDEBAR_MAX_L)
  // O padrão do Ignite escurece ~30% o menu no tema escuro.
  const l = dark ? Math.max(0.1, limited * 0.71) : limited
  const accent = format({ l: l + 0.1, c: Math.min(base.c * 1.45, 0.14), h: base.h })

  return {
    '--sidebar': format({ l, c: base.c, h: base.h }),
    '--sidebar-foreground': format({ l: 0.82, c: Math.min(base.c, 0.05), h: base.h }),
    '--sidebar-accent': accent,
    '--sidebar-border': accent,
  }
}

/**
 * Tokens que o tenant sobrescreve em cada tema. Sem nenhuma cor válida devolve
 * mapas vazios e a paleta padrão do `index.css` continua valendo.
 */
export function buildPalette({ primary, primaryDark, sidebar }: PaletteInput): Palette {
  const palette: Palette = { light: {}, dark: {} }
  const lightBase = isHexColor(primary) ? linearToOklch(hexToLinear(primary)) : null
  const darkBase = isHexColor(primaryDark)
    ? linearToOklch(hexToLinear(primaryDark))
    : lightBase && { ...lightBase, l: Math.max(lightBase.l, DARK_PRIMARY_MIN_L) }

  if (lightBase && isHexColor(primary)) {
    Object.assign(palette.light, brandTokens(lightBase, primary.toLowerCase(), false))
  }
  if (darkBase) {
    // Hex informado vale como está; a derivada passa por `format` para caber na gama.
    const value = isHexColor(primaryDark) ? primaryDark.toLowerCase() : format(darkBase)
    Object.assign(palette.dark, brandTokens(darkBase, value, true))
  }
  if (isHexColor(sidebar)) {
    const base = linearToOklch(hexToLinear(sidebar))
    Object.assign(palette.light, sidebarTokens(base, false))
    Object.assign(palette.dark, sidebarTokens(base, true))
  }

  return palette
}

const declarations = (tokens: PaletteTokens) =>
  Object.entries(tokens)
    .map(([name, value]) => `${name}:${value}`)
    .join(';')

/**
 * CSS global com a paleta do tenant. `html:root` ganha de `:root` e `.dark`
 * do `index.css` independentemente da ordem em que as folhas são carregadas.
 */
export function buildPaletteCss(input: PaletteInput): string {
  const { light, dark } = buildPalette(input)
  const blocks: string[] = []

  if (Object.keys(light).length) blocks.push(`html:root{${declarations(light)}}`)
  if (Object.keys(dark).length) blocks.push(`html:root.dark{${declarations(dark)}}`)

  return blocks.join('\n')
}
